import { MouseEvent, ReactNode, useState } from 'react';
import moment from 'moment';
import cn from 'classnames';
import { ClickOutside } from '../common/ClickOutside';
import { DateTimeStamp } from '../../types/primitives/DateTimeStamp';
import { arrayUtils } from '../../utils/array.utils';
import IconSVG from '../../styles/svg-icons';

interface Props {
    minuteStep?: number
    placeholder?: string
    disabledHours?: number[]
    disabledMinutes?: number[]
    className?: string
    time?: DateTimeStamp | Date | null
    emptyEnabled?: boolean
    children?: (popoverContent: ReactNode) => ReactNode
    onTimeChange?: (time?: DateTimeStamp) => void
}

type AmPm = 'AM' | 'PM'

export default function TimePickerControl({
    minuteStep = 15, placeholder, time, disabledHours = [], children,
    disabledMinutes = [], className, emptyEnabled = false, onTimeChange }: Props) {
    const [popupVisible, setPopupVisible] = useState(false);

    const momentTime = time ? moment.utc(time) : null

    const hour = momentTime ? Number(momentTime.format('h')) : undefined;
    const minute = momentTime?.minute();
    const amPm: AmPm | undefined = momentTime ? (momentTime.hour() >= 12 ? 'PM' : 'AM') : undefined;

    const minutes = arrayUtils.arrayFromRange(0, 59)
        .filter(m => !m || !minuteStep || m % minuteStep === 0)

    const handleChange = (timestamp: string) =>
        onTimeChange?.(moment.utc(timestamp, 'h:m a', true).toISOString());

    const handleClear = (e: MouseEvent) => {
        e.stopPropagation();
        onTimeChange?.();
    }

    const renderPopoverContent = () =>
        <div className="time-picker-content">
            <div className="time-picker-column">
                {arrayUtils.arrayFromRange(1, 12).map(h =>
                    <Hour
                        key={h}
                        hour={h}
                        selected={h === hour}
                        disabled={disabledHours.includes(h % 12 + (amPm === 'PM' ? 12 : 0))}
                        onClick={h => handleChange(`${h}:${minute ?? 0} ${amPm ?? 'AM'}`)}
                    />
                )}
            </div>
            <div className="time-picker-column">
                {minutes.map(m =>
                    <Minute
                        key={m}
                        minute={m}
                        selected={m === minute}
                        disabled={disabledMinutes.includes(m)}
                        onClick={m => handleChange(`${hour ?? 12}:${m} ${amPm ?? 'AM'}`)}
                    />
                )}
            </div>
            <div className="time-picker-column">
                {['AM', 'PM'].map(ap =>
                    <AmPmPick
                        key={ap}
                        value={ap as AmPm}
                        selected={ap === amPm}
                        onClick={ap => handleChange(`${hour ?? 12}:${minute ?? 0} ${ap}`)}
                    />
                )}
            </div>
        </div>

    return (
        <div className={cn('time-picker', className)}>
            <div className='form-control time-picker-value' onClick={() => setPopupVisible(true)}>
                {momentTime?.format('h:mm A') ?? <span className="time-picker-placeholder">{placeholder}</span>}
                {emptyEnabled && !!time &&
                    <button className='btn-close' onClick={handleClear}>
                        <IconSVG name='close' width='16' height='16' />
                    </button>}
            </div>
            {
                popupVisible &&
                <ClickOutside className="time-picker-popover" onClick={() => setPopupVisible(false)}>
                    <>
                        {children ? children(renderPopoverContent()) : renderPopoverContent()}
                    </>
                </ClickOutside>
            }
        </div >
    );
}

interface HourProps {
    hour: number
    selected: boolean
    disabled?: boolean
    onClick: (h: number) => void
}

function Hour({ hour, selected, disabled = false, onClick }: HourProps) {
    return (
        <div
            className={cn('item hour', { selected, disabled })}
            onClick={disabled ? undefined : () => onClick(hour)}
        >
            {hour.toString().padStart(2, '0')}
        </div>

    )
}

interface MinuteProps {
    minute: number
    selected: boolean
    disabled?: boolean
    onClick: (m: number) => void

}

function Minute({ minute, selected, disabled = false, onClick }: MinuteProps) {
    return (
        <div
            key={minute}
            className={cn('item minute', { selected, disabled })}
            onClick={() => onClick(minute)}
        >
            {minute.toString().padStart(2, '0')}
        </div>
    );
}

interface AmPmProps {
    value: AmPm
    selected: boolean
    onClick: (amPm: AmPm) => void
}

function AmPmPick({ value, selected, onClick }: AmPmProps) {
    return (
        <div
            key={value}
            className={cn('item am-pm', { selected })}
            onClick={() => onClick(value)}
        >
            {value}
        </div>
    );
}